import { ClientPortfolioPaymentFrequencyEnum } from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { getContractRequiredMinimumRefinement } from "web_myplann/Portfolio/Forms/ContractShared/contractRequiredMinimum";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import {
	type KeyFieldsLockOptions,
	lockableField,
} from "web_myplann/Shared/Validation/lockableField";
import { optionalDateAsString } from "web_myplann/Shared/Validation/optionalDateAsString";
import { optionalPositiveFloat } from "web_myplann/Shared/Validation/optionalPositiveFloat";
import { refineDateOrder } from "web_myplann/Shared/Validation/refineDateOrder";
import { trimmedNullable } from "web_myplann/Shared/Validation/trimmedNullable";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import { z } from "zod";

export const getBuildingSavingsFormSchema = (
	t: TranslateFunction,
	{ lockKeyFields = false }: KeyFieldsLockOptions = {},
) => {
	const maxMessage = t(Resources.Portfolio.AddShared.Validations.maxValue);
	const positiveNumberMessages = {
		positiveMessage: t(
			Resources.Portfolio.AddShared.Validations.positiveNumber,
		),
		maxMessage,
	};

	return z
		.object({
			name: trimmedNullable(
				z.string().max(
					ValidationLimits.TextLength.LongName,
					t(Resources.Portfolio.AddShared.Validations.maxLength, {
						max: ValidationLimits.TextLength.LongName,
					}),
				),
			),
			contractNumber: lockableField(
				trimmedNullable(
					z.string().max(
						ValidationLimits.TextLength.Name,
						t(Resources.Portfolio.AddShared.Validations.maxLength, {
							max: ValidationLimits.TextLength.Name,
						}),
					),
				),
				lockKeyFields,
			),
			producer: lockableField(
				trimmedNullable(
					z.string().max(
						ValidationLimits.TextLength.LongName,
						t(Resources.Portfolio.AddShared.Validations.maxLength, {
							max: ValidationLimits.TextLength.LongName,
						}),
					),
				),
				lockKeyFields,
			),
			product: lockableField(
				trimmedNullable(
					z.string().max(
						ValidationLimits.TextLength.LongName,
						t(Resources.Portfolio.AddShared.Validations.maxLength, {
							max: ValidationLimits.TextLength.LongName,
						}),
					),
				),
				lockKeyFields,
			),
			productCategoryId: lockableField(z.number().nullable(), lockKeyFields),
			signedAt: lockableField(
				optionalDateAsString(DateFormat.Iso),
				lockKeyFields,
			),
			startAt: lockableField(
				optionalDateAsString(DateFormat.Iso),
				lockKeyFields,
			),
			endAt: lockableField(optionalDateAsString(DateFormat.Iso), lockKeyFields),
			payment: optionalPositiveFloat(
				ValidationLimits.Amount.Max,
				positiveNumberMessages,
			),
			paymentFrequency: z.nativeEnum(ClientPortfolioPaymentFrequencyEnum),
			appreciation: z
				.number()
				.gt(0, t(Resources.Portfolio.AddShared.Validations.positiveNumber))
				.nullish()
				.transform((val) => val ?? null),
			stateContribution: z.enum(["Yes", "No"]).nullable(),
			targetAmount: optionalPositiveFloat(
				ValidationLimits.Amount.Max,
				positiveNumberMessages,
			),
		})
		.superRefine(
			refineDateOrder({
				startBeforeSigned: t(
					Resources.Portfolio.AddShared.Validations.startBeforeSigned,
				),
				endBeforeSigned: t(
					Resources.Portfolio.AddShared.Validations.endBeforeSigned,
				),
				endBeforeStart: t(
					Resources.Portfolio.AddShared.Validations.endBeforeStart,
				),
			}),
		)
		.superRefine(getContractRequiredMinimumRefinement({ lockKeyFields }));
};

export type BuildingSavingsFormInput = z.input<
	ReturnType<typeof getBuildingSavingsFormSchema>
>;
export type BuildingSavingsFormOutput = z.output<
	ReturnType<typeof getBuildingSavingsFormSchema>
>;
